import {
  Grid,
  LegacyCard,
  Link,
  SkeletonBodyText,
  SkeletonDisplayText,
  Text,
  TextContainer,
  Tooltip,
} from "@shopify/polaris"
import classNames from "classnames"
import * as CSS from "csstype"
import { observer } from "mobx-react"
import { ProTooltip } from "polaris-enhance"
import { useHistory } from "react-router-dom"

import { IReturnAnalysisData } from "@/api/dashboard/type"
import stores from "@/stores"

import { ArrowDiagonalIcon, ArrowDownIcon } from "../Chart/Icon"
import styles from "./AnalysisChart.module.scss"
export interface CSSProperties extends CSS.Properties<string | number> {
  "--width"?: string
  "--p-text-heading-sm-font-weight"?: string
}

const AnalysisChart = () => {
  const { ReturnRequestsDataStore } = stores
  const { hasCompare, analyzeTop, operationMeta } = ReturnRequestsDataStore
  const history = useHistory()

  const chartList = [
    {
      id: 0,
      content: t("TopReasonsForReturns"),
      children: analyzeTop.return_reasons,
      url: "/analytics/returns-variant",
      tooltip: t("TheMainReasonsWhyCustomersReturnPr"),
      path: "/analytics/return-reasons-list",
    },
    {
      id: 1,
      content: t("TopReturnedProducts"),
      children: analyzeTop.return_variants,
      url: "/analytics/returns-variant",
      tooltip: t("ProductsWithTheHighestNumberOfRetu"),
      path: "/analytics/products-list",
    },
    {
      id: 2,
      content: t("ReturnsBySolution"),
      children: analyzeTop.return_solutions,
      tooltip: t("DistributionOfReturnsByResolutionMe"),
    },
    {
      id: 3,
      content: t("ReturnRequestsByCountry"),
      children: analyzeTop.return_countries,
      tooltip: t("NumberOfReturnRequestsGroupedByCus"),
    },
  ]

  /**
   * 计算条形图宽度
   * @param value 要计算的值
   * @param maxValue 第一组数据中的最大值（用于计算百分比）
   * @returns number 计算后的宽度像素值
   */
  const calculateBarWidth = (value: number, maxValue: number): number => {
    if (!maxValue || maxValue === 0 || value === 0) return 2
    return Math.round((value / maxValue) * 320)
  }

  /**
   * 获取数据中的最大值（用于计算所有条形的基准宽度）
   * @param currentData 当前数据数组
   * @param compareData 比较数据数组
   * @param hasCompare 是否有比较数据
   * @returns number 数据中的最大值
   */
  const getMaxValue = (
    currentData: IReturnAnalysisData[],
    compareData: IReturnAnalysisData[],
    hasCompare: boolean,
  ): number => {
    let maxValue = 0

    // 遍历当前数据
    currentData?.forEach((item) => {
      const currentCount = Number(item.count) || 0
      maxValue = Math.max(maxValue, currentCount)
    })

    // 如果有比较数据，遍历比较数据
    if (hasCompare) {
      compareData?.forEach((item) => {
        const compareCount = Number(item.count) || 0
        maxValue = Math.max(maxValue, compareCount)
      })
    }

    return maxValue
  }
  const loading = ReturnRequestsDataStore.loading.operationLoading
  return (
    <div className={classNames("my-4", styles.AnalysisChart)}>
      <Grid>
        {chartList?.map((item, index) => {
          return (
            <Grid.Cell
              columnSpan={{ xs: 6, sm: 3, md: 3, lg: 6, xl: 6 }}
              key={index}
            >
              <LegacyCard key={item.content}>
                {loading ? (
                  <div className="p-4 w-full flex flex-col gap-4">
                    <TextContainer>
                      <div className="w-[40%]">
                        <SkeletonBodyText lines={1} />
                      </div>
                      <SkeletonDisplayText size="small" />
                      <div className="w-full h-[364px] bg-[#e3e3e3] rounded-lg"></div>
                    </TextContainer>
                  </div>
                ) : (
                  <div className="p-4 w-full flex flex-col gap-4">
                    <div className="flex justify-between items-center">
                      <Tooltip content={item.tooltip}>
                        <span
                          style={
                            {
                              borderBottom:
                                "0.125rem dotted var(--p-color-border-tertiary)",
                              "--p-text-heading-sm-font-weight": "600",
                            } as CSSProperties
                          }
                          className="inline-block cursor-pointer"
                        >
                          <Text variant="headingSm" as="span">
                            {item.content}
                          </Text>
                        </span>
                      </Tooltip>
                      {index < 2 ? (
                        <Link
                          removeUnderline
                          onClick={() => {
                            ReturnRequestsDataStore.setAnalyzeTableDate({
                              day: stores.CommonStore.selectDateTime.analytics
                                .day,
                              subscribed_at_min:
                                stores.CommonStore.selectDateTime.analytics
                                  .subscribed_at_min,
                              subscribed_at_max:
                                stores.CommonStore.selectDateTime.analytics
                                  .subscribed_at_max,
                            })
                            history.push(item.path as string)
                          }}
                        >
                          {t("ViewAll")}
                        </Link>
                      ) : null}
                    </div>
                    <div
                      className={classNames("flex flex-col gap-4", {
                        "min-h-[364px]": item.children?.cur?.length,
                      })}
                    >
                      {item.children?.cur?.length ? (
                        item.children?.cur?.map(
                          (o: IReturnAnalysisData, oIndex) => {
                            // 获取数据中的最大值作为基准
                            const maxValue = getMaxValue(
                              item.children?.cur || [],
                              item.children?.pre || [],
                              Boolean(hasCompare),
                            )

                            const currentCount = Number(o.count) || 0
                            const compareCount =
                              Number(item.children?.pre?.[oIndex]?.count) || 0

                            // 计算当前条形的宽度（基于当前值）
                            const currentBarWidth = calculateBarWidth(
                              currentCount,
                              maxValue,
                            )
                            // 计算比较条形的宽度（基于比较值）
                            const compareBarWidth = calculateBarWidth(
                              compareCount,
                              maxValue,
                            )
                            // 判断是否需要隐藏当前条形和标题
                            const shouldHideCurrent =
                              currentBarWidth === 0 ||
                              (currentBarWidth <= 2 &&
                                !hasCompare &&
                                currentCount === 0)
                            // 只有当不隐藏当前条形时才显示标题名称
                            const shouldShowName = !shouldHideCurrent

                            return (
                              <div key={index} className="flex flex-col gap-1">
                                {shouldShowName ? (
                                  <ProTooltip
                                    truncate
                                    content={o.name}
                                    className="max-w-[320px]"
                                  >
                                    <Text
                                      variant="bodySm"
                                      as="span"
                                      tone="subdued"
                                    >
                                      {o.name}
                                    </Text>
                                  </ProTooltip>
                                ) : null}
                                {!shouldHideCurrent ? (
                                  <div className="flex items-center gap-2 w-full">
                                    <div
                                      className={classNames(
                                        "bg-[#0FACEF] rounded-r-[4px]",
                                        {
                                          "h-4": hasCompare,
                                          "h-9": !hasCompare,
                                        },
                                        styles.Progress,
                                        styles.AnimateProgressByActive,
                                      )}
                                      style={
                                        {
                                          "--width": `${currentBarWidth}px`,
                                        } as CSSProperties
                                      }
                                    ></div>
                                    <div className="shrink-0 flex items-center gap-2">
                                      <Text
                                        as="span"
                                        variant="bodySm"
                                        tone="subdued"
                                      >
                                        {o.count}
                                      </Text>
                                      <Text
                                        as="span"
                                        variant="bodySm"
                                        tone="subdued"
                                      >
                                        ({o.ratio}%)
                                      </Text>
                                      {Math.abs(Number(o.pop_growth_rate)) >
                                        0 && hasCompare ? (
                                        <div className="flex items-center gap-1">
                                          {Number(o.pop_growth_rate) > 0 ? (
                                            <ArrowDiagonalIcon />
                                          ) : (
                                            <ArrowDownIcon />
                                          )}
                                          <Text
                                            as="span"
                                            variant="bodySm"
                                            tone="subdued"
                                          >
                                            {Math.abs(
                                              Number(o.pop_growth_rate),
                                            )}
                                            %
                                          </Text>
                                        </div>
                                      ) : null}
                                    </div>
                                  </div>
                                ) : null}
                                {compareBarWidth > 0 && hasCompare ? (
                                  <div className="flex items-center gap-2 w-full">
                                    <div
                                      className={classNames(
                                        "bg-[#C4E6FB] rounded-r-[4px]",
                                        {
                                          "h-4": hasCompare,
                                          "h-9": !hasCompare,
                                        },
                                        styles.Progress,
                                        styles.AnimateProgress,
                                      )}
                                      style={
                                        {
                                          "--width": `${compareBarWidth}px`,
                                        } as CSSProperties
                                      }
                                    ></div>
                                    <div className="shrink-0 flex items-center gap-2">
                                      <Text
                                        as="span"
                                        variant="bodySm"
                                        tone="subdued"
                                      >
                                        {item.children?.pre[oIndex]?.count}
                                      </Text>
                                    </div>
                                  </div>
                                ) : null}
                              </div>
                            )
                          },
                        )
                      ) : (
                        <div className="h-[396px] flex items-center justify-center">
                          <div>{t("ThereWasNoDataFoundForThisDateRa")}</div>
                        </div>
                      )}
                    </div>
                    {item.children?.cur?.length ? (
                      <div className="flex items-center gap-4 justify-center">
                        <div className="flex items-center gap-2">
                          <div
                            className={classNames(
                              "w-[8px] h-[8px] rounded-full",
                            )}
                            style={{
                              backgroundColor: "#0FACEF",
                            }}
                          ></div>
                          <Text as="span" variant="bodySm">
                            {operationMeta.cur_period}
                          </Text>
                        </div>
                        {hasCompare ? (
                          <div
                            className={classNames("flex items-center gap-2")}
                          >
                            <div
                              className={classNames(
                                "w-[8px] h-[8px] rounded-full",
                              )}
                              style={{
                                backgroundColor: "#C4E6FB",
                              }}
                            ></div>
                            <Text as="span" variant="bodySm">
                              {operationMeta.pre_period}
                            </Text>
                          </div>
                        ) : null}
                      </div>
                    ) : null}
                  </div>
                )}
              </LegacyCard>
            </Grid.Cell>
          )
        })}
      </Grid>
    </div>
  )
}

export default observer(AnalysisChart)
